import React, { useEffect, useRef, Component } from 'react';
import { HashRouter, useNavigate, useLocation } from 'react-router-dom';
import { ConvexProvider, useQuery, useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { useAppStore } from '@/lib/store';
import { checkHealth, fetchModels } from '@/lib/ai/chat';
import { convex, isConvexEnabled } from '@/lib/convex';

import Header from '@components/layout/Header';
import SearchBar from '@components/layout/SearchBar';
import Sidebar from '@components/layout/Sidebar';
import ContextModal from '@components/layout/ContextModal';
import MemoryModal from '@components/layout/MemoryModal';
import LoginView from '@views/auth/LoginView';

import OverviewView from '@views/overview/OverviewView';
import ChatView from '@views/chat/ChatView';
import CalendarView from '@views/calendar/CalendarView';
import ArtifactsView from '@views/artifacts/ArtifactsView';
import CustomizeView from '@views/customize/CustomizeView';
import SettingsView from '@views/settings/SettingsView';
import ConnectorsView from '@views/connectors/ConnectorsView';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-[#232426] text-center px-6">
          <p className="text-[#e17171] font-semibold text-[15px] mb-2">Something went wrong</p>
          <p className="text-[#8f9196] text-[12px] font-mono max-w-lg break-all">{this.state.error.message}</p>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            className="mt-4 px-4 py-2 bg-[#6498CC] text-white text-[13px] rounded hover:bg-[#5282b8]"
          >Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const VIEW_ROUTES = {
  overview:  '/home',
  chats:     '/dashboard/chats',
  calendar:  '/dashboard/calendar',
  artifacts: '/dashboard/artifacts',
  customise: '/dashboard/customize',
  settings:  '/dashboard/settings',
  connectors:'/dashboard/connectors',
  starred:   '/dashboard/starred',
};

const ROUTE_VIEWS = Object.fromEntries(Object.entries(VIEW_ROUTES).map(([v, r]) => [r, v]));

// Convex is the single source of truth — all reactive queries feed the store directly.
// No localStorage — all settings, preferences, and data live in ConvexDB so the
// app works identically on every device and account.
function ConvexDataSync() {
  const {
    userId,
    setApiKeys, setMemories, setContextFiles,
    setCalendarEvents, setConversations, setArtifacts,
    currentModel, customSystemPrompt, chatMode, cloudcallModelId, webgpuModelId,
    isLoggedIn,
    setCurrentModel, setCustomSystemPrompt, setChatMode, setCloudcallModelId, setWebgpuModelId,
  } = useAppStore();

  const convexApiKeys       = useQuery(api.apiKeys.listWithKeys,    { userId });
  const convexMemories      = useQuery(api.memories.listByUser,     { userId });
  const convexContextFiles  = useQuery(api.contextFiles.list,       { userId });
  const convexCalendar      = useQuery(api.calendar.list,           { userId });
  const convexConversations = useQuery(api.conversations.list,      { userId });
  const convexArtifacts     = useQuery(api.artifacts.list,          { userId });
  const convexUser          = useQuery(api.users.get,               isConvexEnabled ? { userId } : 'skip');
  const convexUpdateUser    = useMutation(api.users.update);

  // ── Preferences: load once from Convex, then write back on change ─────────
  const prefsLoadedRef  = useRef(false);
  const prefsSaveTimer  = useRef(null);

  // On first load, populate store from Convex preferences (including auth)
  useEffect(() => {
    if (!convexUser || prefsLoadedRef.current) return;
    prefsLoadedRef.current = true;
    const p = convexUser.preferences || {};
    if (p.currentModel)     setCurrentModel(p.currentModel);
    if (p.chatMode)         setChatMode(p.chatMode);
    if (p.cloudcallModelId) setCloudcallModelId(p.cloudcallModelId);
    if (p.webgpuModelId)    setWebgpuModelId(p.webgpuModelId);
    if ('customSystemPrompt' in p) setCustomSystemPrompt(p.customSystemPrompt);
    // Restore login state from Convex — no localStorage needed
    if (p.isLoggedIn === true) useAppStore.getState().login('gnanies@amazon.com', 'Student@123@#$');
  }, [convexUser]);

  // Whenever a preference changes, debounce-write to Convex
  useEffect(() => {
    if (!prefsLoadedRef.current || !isConvexEnabled) return;
    if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    prefsSaveTimer.current = setTimeout(() => {
      convexUpdateUser({
        userId,
        preferences: { currentModel, customSystemPrompt, chatMode, cloudcallModelId, webgpuModelId, isLoggedIn },
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(prefsSaveTimer.current);
  }, [currentModel, customSystemPrompt, chatMode, cloudcallModelId, webgpuModelId, isLoggedIn]);

  // ── App data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (convexApiKeys) setApiKeys(convexApiKeys.map((k) => ({ ...k, id: k._id, isVisible: false, deleteCount: 0 })));
  }, [convexApiKeys]);

  useEffect(() => {
    if (!convexMemories) return;
    const fromCloud = convexMemories.map((m) => ({ ...m, id: m._id }));
    // Merge: keep locally-created memories (no _id yet) that Convex hasn't confirmed yet
    const localOnly = useAppStore.getState().memories.filter((m) => !m._id);
    setMemories([...fromCloud, ...localOnly]);
  }, [convexMemories]);

  useEffect(() => {
    if (convexCalendar) setCalendarEvents(convexCalendar.map((e) => ({ ...e, id: e._id })));
  }, [convexCalendar]);

  useEffect(() => {
    if (convexContextFiles) setContextFiles(convexContextFiles.map((f) => ({ ...f, id: f._id, deletedCount: f.deletedCount ?? 0 })));
  }, [convexContextFiles]);

  useEffect(() => {
    if (convexConversations) setConversations(convexConversations);
  }, [convexConversations]);

  useEffect(() => {
    if (convexArtifacts) setArtifacts(convexArtifacts);
  }, [convexArtifacts]);

  return null;
}

// Bidirectional sync between activeView (store) and URL hash
function UrlSync() {
  const { activeView } = useAppStore();
  const nav = useNavigate();
  const location = useLocation();

  // Store → URL: when activeView changes, push matching route
  useEffect(() => {
    const target = VIEW_ROUTES[activeView] ?? '/home';
    if (location.pathname !== target) nav(target, { replace: false });
  }, [activeView]);

  // URL → Store: when user navigates back/forward, sync activeView
  useEffect(() => {
    const path = location.pathname;
    // exact match first
    if (ROUTE_VIEWS[path]) {
      useAppStore.getState().setActiveView(ROUTE_VIEWS[path]);
      return;
    }
    // prefix match for /dashboard/chats/:id
    if (path.startsWith('/dashboard/chats')) {
      useAppStore.getState().setActiveView('chats');
    }
  }, [location.pathname]);

  return null;
}

function AppInner() {
  const { activeView, setSystemStatus, setAvailableModels, setCurrentModel, isLoggedIn } = useAppStore();

  useEffect(() => {
    let interval;
    const ping = async () => {
      try { setSystemStatus(await checkHealth()); }
      catch { setSystemStatus({ overall: 'offline', mode: 'local' }); }
    };
    const loadModels = async () => {
      try {
        const { models = [] } = await fetchModels();
        setAvailableModels(models);
        const stored = useAppStore.getState().currentModel;
        if (models.length > 0 && !models.find((m) => m.id === stored)) setCurrentModel(models[0].id);
      } catch {
        const fallback = [
          { id: 'claude-opus-4-7',   name: 'Claude Opus 4.7',   description: 'Most capable Claude model',  apiModel: 'claude-opus-4-7',            provider: 'anthropic',  paid: true,  free: false },
          { id: 'claude-sonnet-4-6', name: 'Claude Sonnet 4.6', description: 'Balanced power and speed',   apiModel: 'claude-sonnet-4-6',          provider: 'anthropic',  paid: true,  free: false },
          { id: 'claude-haiku-4-5',  name: 'Claude Haiku 4.5',  description: 'Fastest Claude model',       apiModel: 'claude-haiku-4-5-20251001',  provider: 'anthropic',  paid: true,  free: false },
          { id: 'gpt-4o',            name: 'GPT-4o',            description: 'OpenAI multimodal flagship', apiModel: 'gpt-4o',                     provider: 'openai',     paid: true,  free: false },
          { id: 'gpt-4o-mini',       name: 'GPT-4o Mini',       description: 'Affordable and fast GPT-4',  apiModel: 'gpt-4o-mini',                provider: 'openai',     paid: true,  free: false },
          { id: 'llama3-70b-8192',   name: 'Llama 3 70B',       description: 'Free via Groq',              apiModel: 'llama3-70b-8192',            provider: 'groq',       paid: false, free: true  },
          { id: 'llama3-8b-8192',    name: 'Llama 3 8B',        description: 'Fast Llama 3 via Groq',      apiModel: 'llama3-8b-8192',             provider: 'groq',       paid: false, free: true  },
          { id: 'openrouter/auto',   name: 'Auto (Best)',        description: 'Routes to best model',       apiModel: 'openrouter/auto',            provider: 'openrouter', paid: true,  free: false },
          { id: 'gemini-2.5-flash',  name: 'Gemini 2.5 Flash',  description: 'Fast and smart Gemini',      apiModel: 'gemini-2.5-flash-preview-05-20', provider: 'gemini', paid: true,  free: false },
          { id: 'gemini-2.0-flash',  name: 'Gemini 2.0 Flash',  description: 'Fast multimodal (free)',     apiModel: 'gemini-2.0-flash',           provider: 'gemini',     paid: false, free: true  },
          { id: 'mistral-large',     name: 'Mistral Large',     description: "Mistral's flagship model",   apiModel: 'mistral-large-latest',       provider: 'mistral',    paid: true,  free: false },
          { id: 'mistral-nemo',      name: 'Mistral Nemo',      description: '12B open model (free)',      apiModel: 'open-mistral-nemo',          provider: 'mistral',    paid: false, free: true  },
          { id: 'deepseek-chat',     name: 'DeepSeek V3',       description: 'Frontier model, very cheap', apiModel: 'deepseek-chat',              provider: 'deepseek',   paid: true,  free: false },
          { id: 'deepseek-reasoner', name: 'DeepSeek R1',       description: 'Chain-of-thought reasoning', apiModel: 'deepseek-reasoner',          provider: 'deepseek',   paid: true,  free: false },
          { id: 'grok-3',            name: 'Grok 3',            description: 'xAI flagship model',         apiModel: 'grok-3',                     provider: 'xai',        paid: true,  free: false },
          { id: 'grok-3-mini',       name: 'Grok 3 Mini',       description: 'Fast and lightweight Grok',  apiModel: 'grok-3-mini',                provider: 'xai',        paid: true,  free: false },
          { id: 'nvidia/llama-3.1-nemotron-70b-instruct', name: 'Nemotron 70B', description: 'NVIDIA tuned Llama', apiModel: 'nvidia/llama-3.1-nemotron-70b-instruct', provider: 'nvidia', paid: true, free: false },
        ];
        setAvailableModels(fallback);
        const stored = useAppStore.getState().currentModel;
        if (!fallback.find((m) => m.id === stored)) setCurrentModel(fallback[0].id);
      }
    };
    ping(); loadModels();
    interval = setInterval(ping, 30000);
    const handleResize = () => useAppStore.getState().setSidebarOpen(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => { clearInterval(interval); window.removeEventListener('resize', handleResize); };
  }, []);

  if (!isLoggedIn) return <LoginView />;

  // Overview is full-screen — no sidebar
  if (activeView === 'overview') {
    return (
      <div className="flex flex-col h-screen overflow-hidden" style={{ backgroundColor: '#232426', color: '#d5d6d9' }}>
        <Header />
        <div className="flex-1 overflow-y-auto">
          <OverviewView />
        </div>
        <ContextModal /><MemoryModal />
      </div>
    );
  }

  const renderView = () => {
    switch (activeView) {
      case 'chats':      return <ChatView />;
      case 'calendar':   return <CalendarView />;
      case 'artifacts':  return <ArtifactsView />;
      case 'customise':  return <CustomizeView />;
      case 'settings':   return <SettingsView />;
      case 'starred':    return <ArtifactsView filterStarred />;
      case 'connectors': return <ConnectorsView />;
      default:           return <ChatView />;
    }
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden min-w-[346px]" style={{ backgroundColor: '#232426', color: '#d5d6d9' }}>
      <Header />
      <SearchBar />
      <div className="flex flex-1 overflow-hidden relative">
        <Sidebar />
        <main className="flex-1 bg-[#232426] flex flex-col overflow-hidden relative w-full h-full">
          {renderView()}
        </main>
      </div>
      <ContextModal /><MemoryModal />
    </div>
  );
}

function AppWithRouter() {
  return (
    <HashRouter>
      <UrlSync />
      <AppInner />
    </HashRouter>
  );
}

export default function App() {
  if (isConvexEnabled && convex) {
    return (
      <ErrorBoundary>
        <ConvexProvider client={convex}>
          <ConvexDataSync />
          <AppWithRouter />
        </ConvexProvider>
      </ErrorBoundary>
    );
  }
  return <ErrorBoundary><AppWithRouter /></ErrorBoundary>;
}
